import { Map, OrderedSet, Set } from 'immutable';

import { StakeType } from 'app/lib/staking/constants';

import { CheckAvailabilitySuccessPayload } from '../actions';
import {
  EntryLimits, PaymentOptionsEntry, RankEntry, RecordOfEntryLimitsType,
  RecordOfPaymentOptionsEntry,
  RecordOfRankEntryType,
} from '../records';
import { AvailabilityStakesDAO, LimitsDAO, PaymentOptionsDAO } from '../types/backend/availableStakes';
import { EntriesBets } from '../types/entries';
import { ExpressMatchPrices } from '../types/stakeTypes/express';

// ---

function createEntryLimits(
  limits?: LimitsDAO | null,
): RecordOfEntryLimitsType {
  const max = limits?.max;

  return EntryLimits({
    min: limits?.min,
    max: (max !== 'Infinity') ? max : Infinity,
  });
}

// ---

type SystemEntry = {
  combinations: number;
} & LimitsDAO & PaymentOptionsDAO;

function createRankEntry(
  system: SystemEntry,
): RecordOfRankEntryType {
  const max = system?.max;

  return RankEntry({
    ...system,
    max: (max !== 'Infinity') ? max : Infinity,
  });
}

// ---

function createEntryPaymentOptions(
  options?: PaymentOptionsDAO | null,
): RecordOfPaymentOptionsEntry {
  return PaymentOptionsEntry({
    isPledgePossible: options?.isPledgePossible,
    isFreeBetPossible: options?.isFreeBetPossible,
    freeBetPossibleStatus: options?.freeBetPossibleStatus,
  });
}

// ---

export function getExpressMatchPrices(
  express: AvailabilityStakesDAO['express'],
): ExpressMatchPrices {
  let matchPrices: ExpressMatchPrices = Map();

  if (!express?.matchPrices) {
    return matchPrices;
  }

  const pricesKeys = Object.keys(express.matchPrices);

  pricesKeys.forEach((key) => {
    matchPrices = matchPrices.set(
      parseInt(key), {
        entryPrice: express.matchPrices[key],
        entityPrice: undefined,
      },
    );
  });

  return matchPrices;
}

// ---

type Singles = CheckAvailabilitySuccessPayload['singles'];
type Express = CheckAvailabilitySuccessPayload['express'];
type Systems = CheckAvailabilitySuccessPayload['systems'];

export function getAvailabilityResult(
  entries: EntriesBets,
  body: AvailabilityStakesDAO,
): CheckAvailabilitySuccessPayload {
  const singleIsAvailable = (
    body.singles.length === entries.size // kinda hack!
  );

  const express: Express = {
    limits: createEntryLimits(body.express),
    matchPrices: getExpressMatchPrices(body.express),
    paymentOptions: createEntryPaymentOptions(body.express),
  };

  const systems: Systems = {
    systemRankOf: null,
    rankEntries: Map(),
  };

  if (body.express) {
    let matchIds = Set();
    for (const [, { entity }] of entries) {
      if (matchIds.has(entity.matchId)) {
        // if there is at least one match with more than one bet exists,
        // then "system" should not be available.
        matchIds = Set();
        break;
      }

      matchIds = matchIds.add(entity.matchId);
    }

    if (matchIds.size > 2) {
      systems.systemRankOf = matchIds.size;
    }
  }

  if (body.systems) {
    body.systems.forEach(({ rank, ...system }) => {
      systems.rankEntries = systems.rankEntries.set(rank, createRankEntry(system));
    });
  }

  const singles: Singles = body.singles.reduce((map, bet) => {
    map.limits = map.limits.set(bet.betId, createEntryLimits(bet));
    map.paymentOptions = map.paymentOptions.set(bet.betId, createEntryPaymentOptions(bet));

    return map;
  }, { limits: Map(), paymentOptions: Map() } as Singles);

  return {
    singles,
    express,
    systems,

    availableStakeTypes: OrderedSet([
      singleIsAvailable && StakeType.SINGLE,
      body.express && StakeType.EXPRESS,
      (systems.systemRankOf !== null) && StakeType.SYSTEM,
    ].filter(Boolean) as StakeType[]),
  };
}

